import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'What is an ADR?',
    a: 'An American Depositary Receipt (ADR) is a US-listed share that represents ownership in a foreign company. Instead of opening a UK or Canadian brokerage account to buy British American Tobacco or Royal Bank of Canada, you can buy their NYSE-listed ADRs (BTI, RY) in any US brokerage. ADRs trade in USD, pay dividends in USD (after currency conversion), and report taxes on a standard 1099-DIV.',
  },
  {
    q: 'How does foreign withholding tax work?',
    a: 'When a foreign company pays you a dividend, the country of origin typically withholds a tax at the source — often 15-30% — BEFORE the cash reaches you. You can usually claim a US Foreign Tax Credit (Form 1116) to offset most of it. UK dividends have 0% withholding under the US-UK treaty. Canadian dividends have 15% withholding. EU countries range 15-30%. The withholding shows up on your 1099-DIV box 7.',
  },
  {
    q: 'Should I hold ADRs in a Roth IRA?',
    a: 'Generally NO. Foreign withholding still applies in a Roth IRA, but you can\'t claim the Foreign Tax Credit because the account is tax-free already. So you LOSE the 15-30% withholding with no offset. Hold ADRs in a TAXABLE account where the Foreign Tax Credit recovers most of the withholding. Exceptions: UK ADRs (0% withholding) and Hong Kong ADRs (0%) are fine in any account.',
  },
  {
    q: 'Does DiviDrip support international tickers?',
    a: 'Yes — DiviDrip carries the major ADRs in its database, including BTI, DEO, RY, ENB, UL, and others. Their yields, ex-dates, and pay dates pull through Massive\'s data feeds the same as US tickers. The Stock Modal labels them as "Foreign / ADR" so you know to expect withholding tax on the dividend.',
  },
];

export default function LearnExUsAristocrats() {
  const jsonLd = {
    '@context': 'https://schema.org', '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })),
  };
  return (
    <LearnPage
      title="Ex-US Dividend Aristocrats — UK and Canadian Royalty Most US Investors Miss | DiviDrip"
      description="British American Tobacco, Diageo, Royal Bank of Canada, Enbridge, Unilever — long streaks, US-accessible via ADRs, often higher yields than US Aristocrats. Withholding tax notes and Roth-IRA caveats included."
      canonical="https://twylightcrow.com/learn/ex-us-dividend-aristocrats"
      guidePath="/learn/ex-us-dividend-aristocrats"
      sectionLabel="Vehicles & Structures"
      sectionAnchor="vehicles"
      kicker="Learn · International picks"
      h1="Ex-US Dividend Aristocrats — UK and Canadian Royalty Most US Investors Miss"
      datePublished="2026-05-24"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/international-dividends', label: 'International dividends — tax and currency basics' },
        { to: '/learn/dividend-kings-vs-aristocrats-vs-achievers', label: 'Kings vs Aristocrats vs Achievers' },
        { to: '/learn/why-drip-roth', label: 'Why DRIP belongs in a Roth IRA' },
        { to: '/learn/qualified-vs-ordinary-dividends', label: 'Qualified vs ordinary dividends' },
        { to: '/learn/bond-yields-vs-dividend-yields', label: 'Bond yields vs dividend yields' },
      ]}
    >
      <p>
        US investors talk about the Dividend Aristocrats — the 65+ S&amp;P 500
        companies that have raised dividends 25+ consecutive years. Few know
        there&apos;s an EQUIVALENT roster of foreign dividend royalty — UK,
        Canadian, and European names with similarly long streaks, equally
        recognisable brands, and often <strong>HIGHER yields</strong>. And
        every one of them is buyable in any US brokerage account via ADRs.
      </p>

      <h2>Five names every US dividend investor should know</h2>
      <p>Live yields verified from the DiviDrip database:</p>
      <table className="learn-table">
        <thead>
          <tr><th>ADR</th><th>Yield</th><th>Country</th><th>What they do</th></tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>BTI</strong></td>
            <td>~5.1%</td>
            <td>UK</td>
            <td>British American Tobacco — Lucky Strike, Camel, Pall Mall. 25+ years of raises.</td>
          </tr>
          <tr>
            <td><strong>ENB</strong></td>
            <td>~5.4%</td>
            <td>Canada</td>
            <td>Enbridge — North American pipeline giant. 29 years of consecutive dividend raises.</td>
          </tr>
          <tr>
            <td><strong>UL</strong></td>
            <td>~3.9%</td>
            <td>UK / Netherlands</td>
            <td>Unilever — Dove, Hellmann&apos;s, Ben &amp; Jerry&apos;s. Steady consumer staples.</td>
          </tr>
          <tr>
            <td><strong>DEO</strong></td>
            <td>~3.9%</td>
            <td>UK</td>
            <td>Diageo — Johnnie Walker, Smirnoff, Guinness. Global spirits dominance.</td>
          </tr>
          <tr>
            <td><strong>RY</strong></td>
            <td>~2.7%</td>
            <td>Canada</td>
            <td>Royal Bank of Canada — largest Canadian bank, 14+ years of raises.</td>
          </tr>
        </tbody>
      </table>
      <p>
        Average yield across these five: roughly <strong>4.2%</strong> —
        meaningfully higher than the US Aristocrat average around 2.5%. Brand
        recognition is at least equal. Several have streaks that beat most US
        names.
      </p>

      <h2>The withholding tax wrinkle</h2>
      <p>
        Here&apos;s the catch US investors need to understand. When a foreign
        company pays you a dividend, the country of origin typically withholds
        tax BEFORE the cash reaches you. You can&apos;t avoid it. But you usually
        CAN claim it back via the US Foreign Tax Credit (Form 1116).
      </p>
      <table className="learn-table">
        <thead>
          <tr><th>Country</th><th>Withholding rate</th><th>Treaty notes</th></tr>
        </thead>
        <tbody>
          <tr><td>United Kingdom</td><td><strong>0%</strong></td><td>US-UK treaty exempts dividends. BTI, DEO, UL — clean.</td></tr>
          <tr><td>Hong Kong</td><td>0%</td><td>No withholding. ADRs from this jurisdiction are clean.</td></tr>
          <tr><td>Canada</td><td>15%</td><td>RY, ENB. Recoverable via Foreign Tax Credit in TAXABLE accounts.</td></tr>
          <tr><td>Switzerland</td><td>35% (reclaim to 15%)</td><td>Nestlé, Novartis — paperwork involved.</td></tr>
          <tr><td>Most EU countries</td><td>15-30%</td><td>Variable; treaty-dependent. Check before buying.</td></tr>
        </tbody>
      </table>

      <h2>The Roth IRA trap</h2>
      <p>
        Foreign withholding still applies in a Roth IRA. But you CAN&apos;T
        claim the Foreign Tax Credit because the Roth is already tax-free —
        there&apos;s no US tax owed to credit AGAINST. So you LOSE the 15-30%
        withholding outright.
      </p>
      <p>
        Practical recommendations:
      </p>
      <ul>
        <li>
          <strong>UK ADRs (BTI, DEO, UL) — fine anywhere.</strong> 0%
          withholding means no Roth penalty. They&apos;re actually great Roth
          holdings.
        </li>
        <li>
          <strong>Canadian ADRs (RY, ENB, TD, BMO) — TAXABLE account
          only.</strong> The 15% withholding is recoverable via Foreign Tax
          Credit. Lose that recovery in a Roth and you&apos;re leaving 15% of
          dividends on the table.
        </li>
        <li>
          <strong>European ADRs — check rate first.</strong> Switzerland is the
          worst (35%, partly reclaimable). Germany and France 15-30%. Use
          taxable accounts only.
        </li>
      </ul>

      <h2>Why most US investors miss these</h2>
      <ol>
        <li>
          <strong>The standard Aristocrat list is S&amp;P 500-only.</strong>{' '}
          So BTI, ENB, DEO never appear on it — even though they each have
          longer streaks than most US Aristocrats.
        </li>
        <li>
          <strong>Withholding-tax fear.</strong> Investors hear &quot;foreign
          tax&quot; and skip. They don&apos;t realise UK is 0% or that Canadian
          is fully recoverable in taxable accounts.
        </li>
        <li>
          <strong>ADRs feel exotic.</strong> They&apos;re not — they trade like
          any US stock, in USD, with normal liquidity and standard tax
          reporting.
        </li>
        <li>
          <strong>Mediocre research coverage.</strong> Most retail
          dividend-investing content is US-centric. Foreign names get one
          paragraph and a vague &quot;watch out for taxes&quot; warning.
        </li>
      </ol>

      <h2>Building an international sleeve</h2>
      <p>
        A simple approach: dedicate 10-20% of your dividend portfolio to ex-US
        names. Within that sleeve:
      </p>
      <ul>
        <li>
          <strong>UK Aristocrats in your Roth IRA</strong> (BTI, DEO, UL — 0%
          withholding, qualified dividends, full Roth benefit).
        </li>
        <li>
          <strong>Canadian Aristocrats in your taxable account</strong> (RY,
          ENB, TD — 15% withholding recoverable via Foreign Tax Credit).
        </li>
        <li>
          <strong>Skip European names unless you understand the reclaim
          process</strong> — the paperwork can eat the yield advantage.
        </li>
      </ul>
      <p>
        See the existing{' '}
        <Link to="/learn/international-dividends">International Dividends
        guide</Link> for the full mechanics of currency conversion, reporting,
        and dividend timing differences (most foreign payers go semi-annual,
        not quarterly).
      </p>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>Bottom line</h2>
      <p>
        The S&amp;P 500 Aristocrat list is a great starting point — and a
        seriously incomplete one. BTI, ENB, DEO, UL, RY all have multi-decade
        dividend records, recognisable brands, and yields that often beat their
        US peers. The withholding-tax footnote is manageable if you place them
        in the right account. Skipping them entirely is leaving real income on
        the table.
      </p>
    </LearnPage>
  );
}
